import { Badge, Box, Paper, Table } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ReactNode } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { Header } from "shared/components/global/Header";
import { formatIpawsChannel } from "shared/components/ipaws/format";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { graphql } from "src/gql";
import { IpawsCertificateType, IpawsChannel } from "src/gql/graphql";
import { match } from "ts-pattern";

const dateFormatOptions = {
    month: "numeric" as const,
    day: "numeric" as const,
    year: "numeric" as const,
    hour: "numeric" as const,
    minute: "numeric" as const,
};

const TableRow = (props: { label: ReactNode; value: ReactNode }) => {
    const isMobile = useIsMobile();
    return (
        <Table.Tr>
            <Table.Th w={isMobile ? 120 : undefined} fw={600}>
                {props.label}
            </Table.Th>
            <Table.Td>{props.value}</Table.Td>
        </Table.Tr>
    );
};

const DetailsTable = () => {
    const { certId } = Route.useParams();

    const cert = useSuspenseQuery(queries.cert(certId));
    const certData = cert.data.ipawsCertificate;

    return (
        <>
            <Header>Details</Header>
            <Paper withBorder>
                <Table variant="vertical" withTableBorder>
                    <Table.Tbody>
                        <TableRow label="Certificate Name" value={certData.name} />
                        <TableRow label="Description" value={certData.description} />
                        <TableRow label="COG ID" value={certData.cogId} />
                        <TableRow
                            label="Enabled"
                            value={certData.enabled ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>}
                        />
                        <TableRow
                            label="Type"
                            value={
                                <Badge
                                    color={match(certData.type)
                                        .with(IpawsCertificateType.Live, () => "red")
                                        .with(IpawsCertificateType.Test, () => "blue")
                                        .exhaustive()}
                                >
                                    {certData.type}
                                </Badge>
                            }
                        />
                        <TableRow label="Category" value={certData.category} />
                        <TableRow label="Email" value={certData.email} />
                        <TableRow label="Organization" value={certData.organization} />
                        <TableRow
                            label="Expiration Date"
                            value={new Date(parseInt(certData.expireAt)).toLocaleString("en-US", dateFormatOptions)}
                        />
                        <TableRow
                            label="Last Successful Test Date"
                            value={new Date(parseInt(certData.testSucceededAt)).toLocaleString("en-US", dateFormatOptions)}
                        />
                    </Table.Tbody>
                </Table>
            </Paper>
        </>
    );
};

const allChannels = Object.values(IpawsChannel);

const CodeTable = (props: { header: string; data: Map<string, Set<IpawsChannel>> }) => {
    const columnWidth = `calc(100% / ${allChannels.length + 1})`;
    return (
        <>
            <Header>{props.header}</Header>
            <Paper withBorder style={{ overflowX: "auto" }}>
                <Table withTableBorder withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th w={columnWidth}>{props.header}</Table.Th>
                            {allChannels.map((c) => (
                                <Table.Th key={c} w={columnWidth} style={{ textAlign: "center" }}>
                                    {formatIpawsChannel(c)}
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {Array.from(props.data).map(([code, channels]) => (
                            <Table.Tr key={code}>
                                <Table.Td>{code}</Table.Td>
                                {allChannels.map((ch) => (
                                    <Table.Td key={code + ch}>
                                        {channels.has(ch) && (
                                            <Box style={{ display: "flex", justifyContent: "center" }} py="xs">
                                                <IconCircleCheck />
                                            </Box>
                                        )}
                                    </Table.Td>
                                ))}
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>
        </>
    );
};

const EventCodesTable = () => {
    const { certId } = Route.useParams();

    const cert = useSuspenseQuery(queries.cert(certId));
    const certData = cert.data.ipawsCertificate;

    const eventCodes = new Map<string, Set<IpawsChannel>>();
    certData.channels.forEach((ch) =>
        ch.eventCodes.forEach((geo) => {
            const channelSet = eventCodes.get(geo.id);
            if (!!channelSet) {
                channelSet.add(ch.channel);
                eventCodes.set(geo.id, channelSet);
            } else {
                eventCodes.set(geo.id, new Set<IpawsChannel>([ch.channel]));
            }
        }),
    );

    return <CodeTable header="Event Codes" data={eventCodes} />;
};

const GeoCodesTable = () => {
    const { certId } = Route.useParams();

    const cert = useSuspenseQuery(queries.cert(certId));
    const certData = cert.data.ipawsCertificate;

    const geoCodes = new Map<string, Set<IpawsChannel>>();
    certData.channels.forEach((ch) =>
        ch.geoCodes.forEach((geo) => {
            const channelSet = geoCodes.get(geo.code);
            if (!!channelSet) {
                channelSet.add(ch.channel);
                geoCodes.set(geo.code, channelSet);
            } else {
                geoCodes.set(geo.code, new Set<IpawsChannel>([ch.channel]));
            }
        }),
    );

    return <CodeTable header="Geocodes" data={geoCodes} />;
};

const Page = () => {
    return (
        <Content paper>
            <Content.Heading backable>Certificate Details</Content.Heading>
            <DetailsTable />
            <EventCodesTable />
            <GeoCodesTable />
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/certificates/$certId/details")({
    component: Page,
});

const queries = {
    cert: (id: string) =>
        queryOptions({
            queryKey: ["ipaws", "certifications", "details", id],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsCertificateDetails($id: UUID!) {
                            ipawsCertificate(id: $id) {
                                id
                                name
                                description
                                cogId
                                enabled
                                type
                                category
                                email
                                organization
                                expireAt
                                testSucceededAt
                                channels {
                                    channel
                                    geoCodes {
                                        code
                                    }
                                    eventCodes {
                                        id
                                    }
                                }
                            }
                        }
                    `),
                    { id },
                ),
        }),
};
